// cSpell:ignore Aangepast Bedrag Berekening Filip Gemeenschappelijke huurde kosten Kosten maanden Privatieve Provisie Provisies Saldo Samenvatting toegevoegd toevoegen unticks verwarming Voorschot Zelf
// The grouped table of the wizard's cards (Lewis, 2026-10-16): a party (a
// tenant or an owner) on a row of its own, its lines indented under it. The
// "Provisies" step's cards (with a checkbox per line) and the "Samenvatting"
// and the confirmed view's cards (without) are both this table, on the same
// columns, rows, borders and fonts, so the steps cannot drift apart
// (Lewis, 2026-10-17):
//
//   ☑ Provisie                       Berekening                 Bedrag
//   ☑ Filip  huurde tot 30/06/2026 · 6 van de 12 maanden ⓘ    € 500,00      ← party
//       ☑ Gemeenschappelijke kosten  € 55,00 × 6 maanden ⓘ    € 330,00 ✎
//       ☑ Privatieve kosten          € 20,00 × 6 maanden ⓘ    € 120,00 ✎
//       ☑ Voorschot verwarming       Zelf toegevoegd           € 50,00 ✎ 🗑
//       ⊕ Provisie toevoegen
//
// Every amount ends at the same right edge (Lewis, 2026-10-17): in a table
// with actions, after each amount comes a fixed area for a line's pencil
// and bin (ProvisionAmount), kept empty where a line has no bin or no
// pencil, and after the heading and the party's subtotal too.
//
// Each party heads its own lines on a light green row with its subtotal;
// each line is one compact row whose label sits indented, so the hierarchy
// reads at a glance. A group heads the lines under it ("Gemeenschappelijke
// kosten"); a total sums them ("Saldo"). The party's
// and the card's checkboxes are tri-state: all, some or none of their lines.
//
// DataTable cannot render a row that spans columns, a row of another
// level, nor a tri-state checkbox, so this table is built from its pieces:
// its Cell for the headings and its Checkbox, on the same borders and fonts,
// on the shared column widths (provisionColumns.ts) so the cards line up.

import { Cell, Checkbox, Icon } from "@rentiohq/web-shared/dist/components";
import styled, {
  borderRadius,
  FontWeights,
  spacing,
} from "@rentiohq/web-theme";
import React from "react";
import { TLinesSelectState } from "../../../../provisionLineInclusion.utils";
import {
  CELL_PADDING,
  ILinesTableColumn,
  PROVISION_COLUMNS,
} from "./provisionColumns";

// The checkbox column before the columns.
const SELECT_COLUMN_WIDTH = 40;
// How far a line's checkbox sits in from the party's.
const LINE_INDENT = 8;
// The width of a checkbox and the gap after it: a line's text starts there.
const CHECKBOX_WIDTH = 24;
const CHECKBOX_GAP = 8;
// Without checkboxes: how far a line sits in from its party, and a line
// under a group from that group, where a checkbox table's text starts.
const PLAIN_INDENT = LINE_INDENT * 2;
const PLAIN_NESTED_INDENT = LINE_INDENT + CHECKBOX_WIDTH + CHECKBOX_GAP;
// The space before each party after the first.
const PARTY_GAP = 8;
// A cell's padding; the card's totals keep the same distance from its edge.
export { CELL_PADDING };
// One action after a line's amount: a plain Button around a small icon
// (16px and its 1px border on either side), and the gap between the
// actions, the one after the amount too.
const ACTION_WIDTH = 18;
const ACTION_GAP = 6;
const DASH_WIDTH = 10;
// The add button's own padding: its icon sits where a line's text starts.
const ADD_BUTTON_PADDING_X = 8;
// The room under the add button, before the next party.
const FOOTER_PADDING_BOTTOM = 6;

// Whether the amounts of a table get the area for a line's actions.
const LinesTableContext = React.createContext({ hasActions: true });

const Table = styled.table<{ columnWidths: string[]; selectable: boolean }>`
  width: 100%;
  border-spacing: 0;
  table-layout: fixed;

  /* A word or a date is never split over two lines ("16/10/202 6"). */
  th,
  td,
  th *,
  td * {
    word-break: normal;
    overflow-wrap: normal;
  }
  ${({ selectable }) =>
    selectable
      ? `thead th:first-child {
    width: ${SELECT_COLUMN_WIDTH}px;
  }`
      : ""}
  ${({ columnWidths, selectable }) =>
    columnWidths
      .map(
        (width, index) => `
  thead th:nth-child(${index + (selectable ? 2 : 1)}) {
    width: ${width};
  }`,
      )
      .join("")}
`;

const BodyCell = styled.td<{ isNumeric?: boolean }>`
  padding: ${CELL_PADDING};
  vertical-align: middle;
  line-height: 1.3;
  ${({ isNumeric }) => (isNumeric ? "text-align: right;" : "")}
`;

// A party's row: a light tint of the brand green with a thin green edge on
// the left, so the parties stand apart from their (white) lines. The tint
// is the one DataTable gives a selected row (green10); the edge is drawn
// inside the first cell, so nothing shifts.
const PARTY_ACCENT_WIDTH = 3;

const PartyRow = styled.tr`
  ${BodyCell} {
    background: ${({ theme }) => theme.colors.green10};
  }
  ${BodyCell}:first-child {
    box-shadow: inset ${PARTY_ACCENT_WIDTH}px 0 0
      ${({ theme }) => theme.colors.green60};
  }
`;

const SpacerRow = styled.tr`
  td {
    height: ${PARTY_GAP}px;
    padding: 0;
  }
`;

const LineRow = styled.tr<{ isClickable?: boolean }>`
  ${BodyCell} {
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral20};
  }
  ${({ isClickable, theme }) =>
    isClickable
      ? `
    cursor: pointer;
    &:hover ${BodyCell} {
      background: ${theme.colors.neutral10};
    }`
      : ""}
`;

// The button to add a provision by hand, on a compact row of its own.
const FooterRow = styled.tr`
  ${BodyCell} {
    padding-top: ${spacing("extraTight")};
    padding-bottom: ${FOOTER_PADDING_BOTTOM}px;
  }
`;

// A line's first cell: its checkbox indented together with its label.
const LineLabel = styled.div<{ indent: number }>`
  display: flex;
  align-items: flex-start;
  gap: ${CHECKBOX_GAP}px;
  padding-left: ${({ indent }) => indent}px;
`;

// Room for a checkbox where a line has none, so every label lines up.
const CheckboxSlot = styled.div`
  flex: 0 0 ${CHECKBOX_WIDTH}px;
  height: ${CHECKBOX_WIDTH}px;
`;

const LabelText = styled.div<{ isTotal?: boolean; isGroup?: boolean }>`
  min-width: 0;
  padding-top: 3px;
  ${({ isTotal, isGroup }) =>
    isTotal || isGroup ? `font-weight: ${FontWeights.SemiBold};` : ""}
`;

// The button to add a provision by hand: its icon where a line's text
// starts, its hover background reaching just before it.
const ADD_BUTTON_INDENT =
  LINE_INDENT + CHECKBOX_WIDTH + CHECKBOX_GAP - ADD_BUTTON_PADDING_X;

const FooterIndent = styled.div<{ indent: number }>`
  padding-left: ${({ indent }) => indent}px;
`;

// A quiet text button (Lewis chose it, 2026-10-16: the outline Button in
// capitals was too heavy): a plus in a circle and the text in sentence case,
// in the brand green, without a border or a fill until it is hovered or
// focused, then on the party row's light green. The dashboard's Button
// writes every appearance in capitals, so this is a plain <button> on the
// theme's tokens; its text is its accessible name.
const QuietButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: ${spacing("extraTight")} ${ADD_BUTTON_PADDING_X}px;
  border: 0;
  border-radius: ${borderRadius()};
  background: transparent;
  color: ${({ theme }) => theme.getColor("green")};
  font-family: inherit;
  font-size: 13px;
  font-weight: ${FontWeights.Medium};
  line-height: 16px;
  text-transform: none;
  letter-spacing: normal;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.green10};
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.green60};
    outline-offset: 1px;
  }
`;

export const AddProvisionButton: React.FC<{
  label: string;
  onClick: () => void;
}> = ({ label, onClick }) => (
  <QuietButton type="button" onClick={onClick}>
    <Icon source="addCircle" size="small" />
    <span>{label}</span>
  </QuietButton>
);

// An amount and, after it, the area for a line's actions: two spots of
// the same width, the pencil's and then the bin's, each left empty where a
// line has no such action. Every amount of the table goes through it (the
// heading, the parties' subtotals, every line, and the card's totals under
// the table), so all of them end at one right edge. A table without
// actions (the summary) leaves the area out, everywhere at once.
const AmountWithActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${ACTION_GAP}px;
`;

// The amount and what comes before it ("Aangepast"), on the right; what
// comes before goes onto a line of its own above the amount when the
// column is too narrow for both.
const AmountText = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: flex-end;
  column-gap: ${ACTION_GAP}px;
  min-width: 0;
  text-align: right;
`;

const Actions = styled.div`
  display: flex;
  flex: 0 0 auto;
  gap: ${ACTION_GAP}px;
`;

const ActionSlot = styled.div`
  display: flex;
  justify-content: center;
  width: ${ACTION_WIDTH}px;
`;

export const ProvisionAmount: React.FC<{
  // The amount as text, or its input while it is changed.
  amount: React.ReactNode;
  // Before the amount: "Aangepast" on an amount typed in.
  before?: React.ReactNode;
  // The line's pencil and its bin.
  edit?: React.ReactNode;
  remove?: React.ReactNode;
}> = ({ amount, before, edit, remove }) => {
  const { hasActions } = React.useContext(LinesTableContext);
  return (
    <AmountWithActions>
      <AmountText>
        {before}
        {amount}
      </AmountText>
      {hasActions && (
        <Actions>
          <ActionSlot>{edit}</ActionSlot>
          <ActionSlot>{remove}</ActionSlot>
        </Actions>
      )}
    </AmountWithActions>
  );
};

// The "some" state of a tri-state checkbox: the empty box with a dash.
const SomeWrap = styled.span`
  position: relative;
  display: inline-block;
  line-height: 0;
`;

const SomeDash = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${DASH_WIDTH}px;
  height: 2px;
  margin: -1px 0 0 -${DASH_WIDTH / 2}px;
  border-radius: 1px;
  pointer-events: none;
  background: ${({ theme }) => theme.getColor("green")};
`;

export interface IProvisionSelect {
  state: TLinesSelectState;
  // Ticks (true) or unticks (false) every line it covers.
  onChange: (next: boolean) => void;
}

export interface IProvisionTableLine {
  key: string;
  // Absent for a row without anything to tick ("no provision to settle").
  select?: { checked: boolean; onChange: (next: boolean) => void };
  label: React.ReactNode;
  calculation?: React.ReactNode;
  // A ProvisionAmount, with the line's pencil and bin if it has them.
  amount?: React.ReactNode;
  // "line" (the default); "group": a heading over the lines that follow
  // it, its label over the label and calculation columns; "total": a line
  // that sums the ones above it ("Kosten incl. btw", "Saldo").
  variant?: "line" | "group" | "total";
  // A line under a group heading (in a table without checkboxes it sits a
  // step further in).
  isNested?: boolean;
  // Opens what the line is about (a cost's detail).
  onClick?: () => void;
}

export interface IProvisionTableTenant {
  key: string;
  // Their name, the period this settlement counts, the parents on the lease.
  header: React.ReactNode;
  // The subtotal of their lines, or their balance.
  subtotal: React.ReactNode;
  // Every line of the party at once; absent when they have none.
  select?: IProvisionSelect;
  lines: IProvisionTableLine[];
  // Under the lines: the button to add a provision by hand, or notes.
  footer?: React.ReactNode;
}

const stopAnd = (run: () => void) => (event: React.MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
  run();
};

// DataTable's Checkbox, with a dash for "some": ticking it ticks them all.
const SelectBox: React.FC<{ select?: IProvisionSelect }> = ({ select }) => {
  if (!select) return null;
  const { state, onChange } = select;
  const box = (
    <Checkbox
      onChangeValue={stopAnd(() => onChange(state !== "all"))}
      checked={state === "all"}
      appearance={state === "none" ? "default" : "success"}
      error={false}
    />
  );
  if (state !== "some") return box;
  return (
    <SomeWrap>
      {box}
      <SomeDash />
    </SomeWrap>
  );
};

// One line, group heading or total under a party.
const LineRows: React.FC<{
  line: IProvisionTableLine;
  selectable: boolean;
  columnCount: number;
}> = ({ line, selectable, columnCount }) => {
  const variant = line.variant ?? "line";
  const plainIndent = line.isNested ? PLAIN_NESTED_INDENT : PLAIN_INDENT;
  const indent = selectable ? LINE_INDENT : plainIndent;
  const label = (
    <LineLabel indent={indent}>
      {selectable && variant !== "group" && (
        <CheckboxSlot>
          {line.select && (
            <Checkbox
              onChangeValue={stopAnd(
                () => line.select?.onChange(!line.select.checked),
              )}
              checked={line.select.checked}
              appearance={line.select.checked ? "success" : "default"}
              error={false}
            />
          )}
        </CheckboxSlot>
      )}
      <LabelText isGroup={variant === "group"} isTotal={variant === "total"}>
        {line.label}
      </LabelText>
    </LineLabel>
  );
  return (
    <LineRow isClickable={!!line.onClick} onClick={line.onClick}>
      {selectable && <BodyCell />}
      {variant === "group" ? (
        <BodyCell colSpan={columnCount - 1}>{label}</BodyCell>
      ) : (
        <>
          <BodyCell>{label}</BodyCell>
          <BodyCell>{line.calculation}</BodyCell>
        </>
      )}
      <BodyCell isNumeric>{line.amount}</BodyCell>
    </LineRow>
  );
};

// The grouped table on `columns`: a checkbox column first when the table
// selects (`selectable`), the area for a line's actions after every amount
// when it has actions.
export const LinesTable: React.FC<{
  columns: readonly ILinesTableColumn[];
  headings: React.ReactNode[];
  parties: IProvisionTableTenant[];
  selectable?: boolean;
  hasActions?: boolean;
  // The card's select-all: every line of every party in it.
  select?: IProvisionSelect;
}> = ({
  columns,
  headings,
  parties,
  selectable = false,
  hasActions = false,
  select,
}) => {
  const columnCount = columns.length;
  const footerIndent = selectable ? ADD_BUTTON_INDENT : 0;
  return (
    <LinesTableContext.Provider value={{ hasActions }}>
      <Table
        columnWidths={columns.map(column => column.width)}
        selectable={selectable}
      >
        <thead>
          <tr>
            {selectable && (
              <Cell
                header
                firstColumn
                contentType="icon"
                horizontalSpacing="tight"
                content={<SelectBox select={select} />}
              />
            )}
            {headings.map((heading, index) => (
              <Cell
                header
                key={`heading-${columns[index].heading}`}
                firstColumn={!selectable && index === 0}
                content={
                  columns[index].contentType === "numeric" ? (
                    <ProvisionAmount amount={heading} />
                  ) : (
                    heading
                  )
                }
                contentType={columns[index].contentType}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {parties.map((party, partyIndex) => (
            <React.Fragment key={party.key}>
              {partyIndex > 0 && (
                <SpacerRow aria-hidden>
                  <td colSpan={columnCount + (selectable ? 1 : 0)} />
                </SpacerRow>
              )}
              <PartyRow>
                {selectable && (
                  <BodyCell>
                    <SelectBox select={party.select} />
                  </BodyCell>
                )}
                <BodyCell colSpan={columnCount - 1}>{party.header}</BodyCell>
                <BodyCell isNumeric>
                  <ProvisionAmount amount={party.subtotal} />
                </BodyCell>
              </PartyRow>
              {party.lines.map(line => (
                <LineRows
                  key={line.key}
                  line={line}
                  selectable={selectable}
                  columnCount={columnCount}
                />
              ))}
              {party.footer && (
                <FooterRow>
                  {selectable && <BodyCell />}
                  <BodyCell colSpan={columnCount}>
                    <FooterIndent indent={footerIndent}>
                      {party.footer}
                    </FooterIndent>
                  </BodyCell>
                </FooterRow>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </LinesTableContext.Provider>
  );
};

// The "Provisies" step's table: a checkbox per line, a pencil and a bin
// after the amounts, on PROVISION_COLUMNS.
export const ProvisionLinesTable: React.FC<{
  headings: React.ReactNode[];
  tenants: IProvisionTableTenant[];
  // The card's select-all: every line of every tenant in it.
  select?: IProvisionSelect;
}> = ({ headings, tenants, select }) => (
  <LinesTable
    columns={PROVISION_COLUMNS}
    headings={headings}
    parties={tenants}
    selectable
    hasActions
    select={select}
  />
);
